import { jsPDF } from "jspdf";
import { FaCertificate, FaDownload } from 'react-icons/fa';

// # Certificate definitions
export const CERTIFICATES = {
  STEP1: {
    id: 'usmle_step1',
    name: 'USMLE Step 1 Mentorship Program',
    description: 'Awarded for successfully completing USMLE Step 1 mentorship program',
    type: 'USMLE_Step_1'
  },
  STEP2: {
    id: 'usmle_step2',
    name: 'USMLE Step 2 Mentorship Program',
    description: 'Awarded for successfully completing USMLE Step 2 mentorship program',
    type: 'USMLE_Step_2'
  }
};

// # Certificate utility functions
export const formatCertificateDate = (dateString) => {
  // # If no date is provided, return current date
  if (!dateString) {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // # Try to parse the date string
  const date = new Date(dateString);
  
  // # Check if date is valid
  if (isNaN(date.getTime())) {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const checkCertificateEligibility = (user, certificateType) => {
  return !user.certificates?.some(cert => cert.type === certificateType);
};

// # Generate certificate PDF
export const generateCertificatePDF = (user, certificate) => {
  // # Construct full name from individual fields
  const fullName = `${user.firstName || ''} ${user.middleName || ''} ${user.lastName || ''}`.trim();
  
  // # Import jsPDF and required fonts
  import('jspdf').then(({ default: jsPDF }) => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // # Get current date for preview or use existing date
    const certificateDate = certificate.issueDate || new Date().toISOString();

    // # Set document properties
    doc.setProperties({
      title: `${certificate.name} - ${fullName}`,
      subject: 'USMLE Mentorship Certificate',
      author: 'The Egyptian Mentorship Program for USMLE',
      keywords: 'certificate, USMLE, mentorship'
    });

    // # Add background pattern
    doc.setFillColor(247, 247, 247);
    doc.rect(0, 0, 297, 210, 'F');

    // # Add decorative borders
    // Outer border - Navy Blue
    doc.setDrawColor(10, 49, 97);
    doc.setLineWidth(2);
    doc.rect(10, 10, 277, 190);

    // Inner border - Gold
    doc.setDrawColor(206, 169, 65);
    doc.setLineWidth(1);
    doc.rect(15, 15, 267, 180);

    // Corner decorations
    const cornerSize = 20;
    // Top left
    doc.setDrawColor(178, 34, 34); // Deep Red
    doc.setLineWidth(1.5);
    doc.line(10, 10, 10 + cornerSize, 10);
    doc.line(10, 10, 10, 10 + cornerSize);
    // Top right
    doc.line(287 - cornerSize, 10, 287, 10);
    doc.line(287, 10, 287, 10 + cornerSize);
    // Bottom left
    doc.line(10, 200 - cornerSize, 10, 200);
    doc.line(10, 200, 10 + cornerSize, 200);
    // Bottom right
    doc.line(287 - cornerSize, 200, 287, 200);
    doc.line(287, 200 - cornerSize, 287, 200);

    // # Create image object and load logo
    const logo = new Image();
    logo.crossOrigin = "Anonymous";
    
    // # Handle logo loading
    logo.onload = () => {
      try {
        // # Add logo to PDF
        doc.addImage(logo, 'PNG', 128, 25, 40, 40);
        
        // # Add decorative line under logo
        doc.setDrawColor(206, 169, 65); // Gold
        doc.setLineWidth(0.5);
        doc.line(98, 70, 198, 70);

        // # Add organization name
        doc.setFont('Times-Roman', 'italic');
        doc.setFontSize(28);
        doc.setTextColor(10, 49, 97); // Navy Blue
        doc.text('The Egyptian Mentorship Program for USMLE', 148.5, 85, {
          align: 'center'
        });

        // # Add certificate title with letter spacing
        doc.setFont('Times-Roman', 'bold');
        doc.setFontSize(42);
        doc.setTextColor(178, 34, 34); // Deep Red
        
        // # Apply letter spacing to "Certificate of Achievement"
        const title = "Certificate of Achievement";
        const letterSpacing = 0.8; // mm between letters
        let xOffset = 148.5 - (doc.getStringUnitWidth(title) * 42 / doc.internal.scaleFactor + (title.length - 1) * letterSpacing) / 2;
        
        for (let i = 0; i < title.length; i++) {
          doc.text(title[i], xOffset, 110); // Reduced Y position from 130 to 110
          xOffset += doc.getStringUnitWidth(title[i]) * 42 / doc.internal.scaleFactor + letterSpacing;
        }

        // # Add decorative line under title
        doc.setDrawColor(206, 169, 65); // Gold
        doc.setLineWidth(0.5);
        doc.line(88, 115, 208, 115); // Adjusted Y position from 135 to 115

        // # Add "This certifies that" text
        doc.setFont('Times-Roman', 'italic');
        doc.setFontSize(16);
        doc.setTextColor(10, 49, 97); // Navy Blue
        doc.text('This certifies that', 148.5, 130, { // Adjusted from 150 to 130
          align: 'center'
        });

        // # Add recipient name
        doc.setFont('Times-Roman', 'bold');
        doc.setFontSize(32);
        doc.setTextColor(10, 49, 97);
        doc.text(`${fullName}`, 148.5, 145, { // Adjusted from 165 to 145
          align: 'center'
        });

        // # Add achievement description
        doc.setFont('Times-Roman', 'italic');
        doc.setFontSize(16);
        doc.setTextColor(10, 49, 97); // Navy Blue
        doc.text(
          'Has demonstrated exceptional dedication and successfully completed',
          148.5,
          160, // Adjusted from 180 to 160
          { align: 'center' }
        );

        // # Add certificate name
        doc.setFont('Times-Roman', 'bold');
        doc.setFontSize(20);
        doc.setTextColor(178, 34, 34); // Deep Red
        doc.text(`${certificate.name}`, 148.5, 175, { // Adjusted from 190 to 175
          align: 'center'
        });

        // # Add award date with decorative elements
        doc.setDrawColor(206, 169, 65); // Gold
        doc.setLineWidth(0.5);
        doc.line(118, 185, 178, 185); // Moved up from 195 to 185
        doc.setFont('Times-Roman', 'italic');
        doc.setFontSize(14);
        doc.setTextColor(10, 49, 97); // Navy Blue
        doc.text(
          `Awarded on ${formatCertificateDate(certificateDate)}`,
          148.5,
          190, // Moved up from 200 to 190
          { align: 'center' }
        );

        // # Save the PDF
        const fileName = `${certificate.type}_${fullName.replace(/\s+/g, '_')}.pdf`;
        doc.save(fileName);
      } catch (error) {
        console.error('Error generating PDF:', error);
        generatePDFWithoutLogo(doc, user, certificate);
      }
    };

    // # Handle logo loading error
    logo.onerror = () => {
      console.warn('Logo loading failed, generating certificate without logo');
      generatePDFWithoutLogo(doc, user, certificate);
    };

    // # Set logo source
    logo.src = `${process.env.PUBLIC_URL}/logo512.png`;
  });
};

// # Fallback function to generate PDF without logo
const generatePDFWithoutLogo = (doc, user, certificate) => {
  // # Construct full name from individual fields
  const fullName = `${user.firstName || ''} ${user.middleName || ''} ${user.lastName || ''}`.trim();

  // # Get current date for preview or use existing date
  const certificateDate = certificate.issueDate || new Date().toISOString();

  // # Add organization name (positioned higher due to missing logo)
  doc.setFont('Times-Roman', 'italic');
  doc.setFontSize(24);
  doc.setTextColor(0, 48, 87);
  doc.text('The Egyptian Mentorship Program for USMLE', 148.5, 40, {
    align: 'center'
  });

  // # Add certificate title
  doc.setFont('Times-Roman', 'bold');
  doc.setFontSize(36);
  doc.text('Certificate of Achievement', 148.5, 70, {
    align: 'center'
  });

  // # Add recipient name
  doc.setFont('Times-Roman', 'italic');
  doc.setFontSize(28);
  doc.text(`${fullName}`, 148.5, 100, {
    align: 'center'
  });

  // # Add achievement description
  doc.setFont('Times-Roman', 'normal');
  doc.setFontSize(16);
  doc.text(
    'has successfully completed the mentorship program and demonstrated',
    148.5,
    115,
    { align: 'center' }
  );
  doc.text('exceptional dedication in achieving:', 148.5, 125, {
    align: 'center'
  });

  // # Add certificate name
  doc.setFont('Times-Roman', 'bold');
  doc.setFontSize(24);
  doc.text(`${certificate.name}`, 148.5, 145, {
    align: 'center'
  });

  // # Add award date
  doc.setFont('Times-Roman', 'italic');
  doc.setFontSize(14);
  doc.text(
    `Awarded on ${formatCertificateDate(certificateDate)}`,
    148.5,
    165,
    { align: 'center' }
  );

  // # Save the PDF
  const fileName = `${certificate.type}_${fullName.replace(/\s+/g, '_')}.pdf`;
  doc.save(fileName);
};
